import React from 'react';

import { ClassNames } from '@emotion/core';
import { Trans } from '@lingui/macro';

import useModal from '@zego/hooks/useModal';
import {
  BREAKPOINTS,
  MAX_CONTENT_WIDTH,
  OUTER_GUTTER,
  getColWidthCalc,
  primaryColor,
  unit,
  white,
} from '@zego/theme';

import H3 from '@zego/components/atoms/H3';
import RegularText from '@zego/components/atoms/RegularText';
import Spacer from '@zego/components/atoms/Spacer';
import UnstyledButton from '@zego/components/button/UnstyledButton';
import CloseIcon from '@zego/components/CloseIcon.svg';
import Link from '@zego/components/Link';

const BrokerContactModal = ({ onRequestClose, isOpen }) => {
  const [Modal] = useModal(isOpen);

  return (
    <ClassNames>
      {({ css }) => (
        <Modal
          onRequestClose={onRequestClose}
          closeTimeoutMS={450}
          shouldCloseOnOverlayClick={true}
          overlayClassName={css({
            overflowY: 'auto',
            display: 'flex',
            alignItems: 'center',
            zIndex: 999999,
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: `rgba(42, 42, 42, 0.8)`,
            opacity: 0,
            '&.ReactModal__Overlay--after-open': {
              transition: 'all 200ms ease-in-out',
              opacity: 1,
            },
            '&.ReactModal__Overlay--before-close': {
              opacity: 0,
            },
            padding: `${OUTER_GUTTER}, 0`,
            [BREAKPOINTS.medium]: {
              padding: `${unit(20)}, 0`,
            },
          })}
          css={{
            transition: 'all 300ms ease-in-out 100ms',
            outline: 'none',
            opacity: 0,
            WebkitTapHighlightColor: 'rgba(0,0,0,0)',
            '&.ReactModal__Content--after-open': {
              opacity: 1,
              padding: `${OUTER_GUTTER}px`,
            },
            '&.ReactModal__Content--before-close': {
              transition: 'all 50ms ease-in-out',
              opacity: 0,
            },
            pointerEvents: 'none',
            // Copies the styles from ContentContainer
            maxWidth: OUTER_GUTTER + MAX_CONTENT_WIDTH + OUTER_GUTTER,
            padding: `0, ${OUTER_GUTTER}`,
            margin: '0 auto',
          }}
        >
          <div
            css={{
              pointerEvents: 'all',
              backgroundColor: white,
              borderRadius: 12,
              [BREAKPOINTS.medium]: {
                margin: '0 auto',
                width: getColWidthCalc(12, 20, 8),
              },
            }}
          >
            <div css={{ display: 'flex', justifyContent: 'flex-end' }}>
              <UnstyledButton
                css={{
                  padding: unit(4),
                  display: 'flex',
                  justifyContent: 'flex-end',
                  position: 'absolute',
                }}
                onClick={onRequestClose}
                aria-label="Close"
              >
                <img
                  alt=""
                  src={CloseIcon}
                  css={{
                    width: 14,
                    height: 14,
                    display: 'block',
                    cursor: 'pointer',
                  }}
                />
              </UnstyledButton>
            </div>
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                padding: `${unit(10)}px ${unit(6)}px`,
              }}
            >
              <H3>
                <Trans id="BrokerContact / Heading">
                  Speak with our experts
                </Trans>
              </H3>
              <Spacer height={unit(2)} />
              <RegularText>
                <Trans id="BrokerContact / Info">
                  Email our team today, and we will get back to you to arrange
                  a suitable time to discuss a quote.
                </Trans>
              </RegularText>
              <Spacer height={unit(2)} />
              <Link
                href="mailto:submissions@zego.com"
                css={{ color: primaryColor(8), fontWeight: 700 }}
              >
                submissions@zego.com
              </Link>
            </div>
          </div>
        </Modal>
      )}
    </ClassNames>
  );
};

export default BrokerContactModal;
